import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col,
  InputGroup,
  Pagination
} from "react-bootstrap";
import {
  FaShoppingCart,
  FaCheck,
  FaTimes,
  FaEnvelope,
  FaTrash,
  FaWhatsapp,
  FaSearch,
  FaFilter
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import ProjectService from "../../../services/projectService";

const ManageProjectOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search, Filter, Pagination States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Approval Modal States
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [expireHours, setExpireHours] = useState("48");
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 on search or filter
  }, [searchTerm, statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await ProjectService.adminGetAllOrders();
      if (response.data && response.data.data && response.data.data.orders) {
        setOrders(response.data.data.orders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      toast.error("Failed to load project orders");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApproveModal = (id) => {
    setSelectedOrderId(id);
    setExpireHours("48");
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    try {
      setApproving(true);
      const res = await ProjectService.adminUpdateOrderStatus(selectedOrderId, 'approved', { expire_hours: parseFloat(expireHours) });
      toast.success("Order approved and secure link generated!");
      loadOrders();
      setShowApproveModal(false);
      
      if (res.data && res.data.data && res.data.data.order) {
        const order = res.data.data.order;
        const subject = encodeURIComponent(`Your Project Order Approved - ${order.project_title}`);
        
        // New Secure Download Link
        const downloadUrl = window.location.origin + '/download-project?token=' + order.download_token;
        
        const body = encodeURIComponent(`Hi ${order.user_name},\n\nYour payment for the project "${order.project_title}" has been verified and approved.\n\nYou can now securely download your ZIP file here:\n${downloadUrl}\n\nPlease note: This highly secure link will expire in ${expireHours} hours and is limited to 3 downloads. Do not share it.\n\nThank you for your purchase!`);
        window.location.href = `mailto:${order.user_email}?subject=${subject}&body=${body}`;
      }
    } catch (error) {
      toast.error("Failed to approve order");
    } finally {
      setApproving(false);
    }
  };

  const updateStatus = async (id, status) => {
    if (status === 'approved') {
      handleOpenApproveModal(id);
      return;
    }

    if (window.confirm(`Are you sure you want to mark this order as ${status}?`)) {
      try {
        await ProjectService.adminUpdateOrderStatus(id, status);
        toast.success(`Order marked as ${status}`);
        loadOrders();
      } catch (error) {
        toast.error("Failed to update status");
      }
    }
  };

  const handleDeleteOrder = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this order?")) {
      try {
        await ProjectService.adminDeleteOrder(id);
        toast.success("Order deleted successfully");
        loadOrders();
      } catch (error) {
        toast.error("Failed to delete order");
      }
    }
  };

  const handleWhatsAppClick = (o) => {
    if (!o.user_phone) return;
    let phone = o.user_phone.replace(/\D/g, '');
    if (phone.length === 10) {
      phone = '91' + phone; // Auto prepend India country code if standard 10 digit
    }
    
    let message = `Hi ${o.user_name},\n\n`;
    if (o.status === 'approved' && o.download_token) {
      const downloadUrl = window.location.origin + '/download-project?token=' + o.download_token;
      message += `Your payment for the project "${o.project_title}" has been verified and approved.\n\nYou can now securely download your ZIP file here:\n${downloadUrl}\n\nThank you for your purchase!`;
    } else {
      message += `Regarding your project order "${o.project_title}"...`;
    }
    
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Pagination & Filtering Logic
  const filteredOrders = orders.filter((o) => {
    const matchesSearch = 
      o.user_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      o.project_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.user_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaShoppingCart className="me-2" /> Project Orders
            </h5>
          </Card.Header>
          <Card.Body>
            <Row className="mb-4">
              <Col md={6} sm={12} className="mb-2 mb-md-0">
                <InputGroup>
                  <InputGroup.Text className="bg-white"><FaSearch className="text-muted"/></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by customer name, email, or project..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3} sm={12}>
                <InputGroup>
                  <InputGroup.Text className="bg-white"><FaFilter className="text-muted"/></InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>

            {filteredOrders.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FaShoppingCart size={40} className="mb-3" />
                <h5>No orders found</h5>
                <p>Try adjusting your search or filters.</p>
              </Alert>
            ) : (
              <>
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>SL</th>
                      <th>User Details</th>
                    <th>Project</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                  <tbody>
                    {paginatedOrders.map((o, index) => (
                      <tr key={o.id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>
                          <strong>{o.user_name}</strong><br/>
                          <small><FaEnvelope className="me-1 text-muted"/>{o.user_email}</small><br/>
                          {o.user_phone && (
                            <small 
                              className="text-success" 
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleWhatsAppClick(o)}
                              title="Send WhatsApp Message"
                            >
                              <FaWhatsapp className="me-1"/>{o.user_phone}
                            </small>
                          )}
                        </td>
                        <td>{o.project_title}</td>
                        <td>₹{o.project_price}</td>
                      <td>
                        <Badge bg={
                          o.status === 'approved' ? 'success' : 
                          (o.status === 'rejected' ? 'danger' : 'warning')
                        }>
                          {o.status.toUpperCase()}
                        </Badge>
                      </td>
                      <td>
                        {o.status === 'pending' && (
                          <>
                            <Button
                              variant="success"
                              size="sm"
                              className="me-2"
                              title="Approve & Send Email"
                              onClick={() => updateStatus(o.id, 'approved')}
                            >
                              <FaCheck /> Approve
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => updateStatus(o.id, 'rejected')}
                            >
                              <FaTimes /> Reject
                            </Button>
                          </>
                        )}
                        {o.status === 'approved' && o.download_token && (
                           <Button
                             variant="outline-info"
                             size="sm"
                             className="me-2"
                             title="Resend Email"
                             onClick={() => {
                                const downloadUrl = window.location.origin + '/download-project?token=' + o.download_token;
                                const subject = encodeURIComponent(`Your Project Link - ${o.project_title}`);
                                const body = encodeURIComponent(`Hi ${o.user_name},\n\nHere is your secure download link:\n${downloadUrl}`);
                                window.location.href = `mailto:${o.user_email}?subject=${subject}&body=${body}`;
                             }}
                           >
                             <FaEnvelope /> Email Link
                           </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Delete Order"
                          onClick={() => handleDeleteOrder(o.id)}
                        >
                          <FaTrash /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1} 
                      onClick={() => handlePageChange(currentPage - 1)} 
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item 
                        key={i + 1} 
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      disabled={currentPage === totalPages} 
                      onClick={() => handlePageChange(currentPage + 1)} 
                    />
                  </Pagination>
                </div>
              )}
            </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Approval Expiration Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Approve Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will generate a highly secure, one-time download link for the customer.</p>
          <Form.Group>
            <Form.Label className="fw-bold">Set Link Expiration Time (Hours)</Form.Label>
            <Form.Control 
              type="number" 
              step="0.1" 
              min="0.1" 
              value={expireHours} 
              onChange={(e) => setExpireHours(e.target.value)} 
              placeholder="e.g. 48"
            />
            <Form.Text className="text-muted">
              For 1 hour, enter 1. For 30 minutes, enter 0.5. Defaults to 48 hours. The link is also strictly limited to 3 downloads to prevent unauthorized sharing.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleApproveConfirm} disabled={approving || !expireHours}>
            {approving ? <Spinner size="sm" /> : <><FaCheck className="me-2" /> Approve & Generate Link</>}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ManageProjectOrders;
